import React, { useState, useRef } from 'react';
import { ProTable, ProColumns, ActionType } from '@ant-design/pro-components';
import { Button, Space, Modal, Typography, DatePicker, message, Card, Tag, Badge, Spin } from 'antd';
import { EyeOutlined, LoadingOutlined } from '@ant-design/icons';
import { useAuth0 } from '@auth0/auth0-react';
import { HistoryAnalysisApi, HistoryRecord, HistoryQueryParams, HistoryRecordWithEvaluation } from '@/services/historyApi';
import dayjs from 'dayjs';
import { handleError } from '@/utils/ui-helpers';
import { useSupabaseAuth } from '@/utils/store/supabaseAuth';

const { Text, Paragraph } = Typography;

const AnalysisPage: React.FC = () => {
  const { user, isAuthenticated } = useSupabaseAuth();
  const actionRef = useRef<ActionType>();
  const [detailModalVisible, setDetailModalVisible] = useState(false);
  const [inputModalVisible, setInputModalVisible] = useState(false);
  const [resultModalVisible, setResultModalVisible] = useState(false);
  const [currentRecord, setCurrentRecord] = useState<HistoryRecord | null>(null);
  const [currentContent, setCurrentContent] = useState<string>('');
  const [evaluatingRecords, setEvaluatingRecords] = useState<Set<string>>(new Set());

  // 公共函数：获取分数颜色
  const getScoreColor = (score: number, maxScore: number) => {
    const percentage = (score / maxScore) * 100;
    if (percentage >= 80) return 'badge-info';
    if (percentage >= 70) return 'badge-warning';
    return 'badge-error';
  };

  // 公共函数：检查是否有有效评测
  const hasValidEvaluation = (record: HistoryRecordWithEvaluation) => {
    return record.hasEvaluation && record.evaluationStatus === 'SUCCESS' && record.totalScore;
  };

  // 公共函数：解析JSON内容
  const parseJsonSafely = (text: string) => {
    try {
      return JSON.parse(text);
    } catch {
      return null;
    }
  };

  // 公共函数：创建内容预览组件
  const createContentPreview = (text: string, onView: () => void, content?: string) => (
    <div className="flex items-start gap-2">
      <div className="text-xs text-gray-700 line-clamp-2 leading-relaxed flex-1">
        {content || text}
      </div>
      <button
        className="btn btn-xs btn-ghost p-1 min-h-0 h-6 w-6 flex-shrink-0"
        onClick={onView}
      >
        <EyeOutlined className="w-3 h-3" />
      </button>
    </div>
  );

  // 表格列定义
  const columns: ProColumns<HistoryRecord>[] = [
    {
      title: 'ID',
      dataIndex: 'id',
      width: 55,
      ellipsis: true,
      copyable: true,
      hideInTable: false,
      render: (text) => (
        <span className="font-mono text-xs text-gray-600 bg-gray-50 px-2 py-1 rounded">
          {text}
        </span>
      ),
    },
    {
      title: '邮箱',
      dataIndex: 'email',
      width: 160,
      ellipsis: true,
      copyable: true,
      hideInTable: false,
      render: (text) => (
        <span className="font-mono text-xs text-gray-600 bg-gray-50 px-2 py-1 rounded">
          {text}
        </span>
      ),
    },
    {
      title: '创建时间',
      dataIndex: 'createTime',
      valueType: 'dateTimeRange',
      width: 80,
      sorter: true,
      search: {
        transform: (value) => {
          return {
            startTime: value[0],
            endTime: value[1],
          };
        },
      },
      render: (_, record) => (
        <div className="text-sm">
          <div className="text-xs font-medium text-gray-900">
            {dayjs(record.createTime).format('MM-DD HH:mm')}
          </div>
          <div className="text-xs text-gray-500">
            {dayjs(record.createTime).format('YYYY')}
          </div>
        </div>
      ),
    },
    
    {
      title: '行业',
      dataIndex: 'input',
      search: false,
      width: 80,
      render: (text) => {
        const inputData = parseJsonSafely(text as string);
        return inputData?.Industry ? (
          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
            {inputData.Industry}
          </span>
        ) : (
          <span className="text-gray-400 text-sm">-</span>
        );
      },
    },
    {
      title: '输出类型',
      dataIndex: 'input',
      search: false,
      width: 120,
      render: (text) => {
        const inputData = parseJsonSafely(text as string);
        return inputData?.Output_type ? (
          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
            {inputData.Output_type}
          </span>
        ) : (
          <span className="text-gray-400 text-sm">-</span>
        );
      },
    },
    {
      title: '输入内容',
      dataIndex: 'input',
      search:false,
      ellipsis: false,
      width: 200,
      render: (text) => {
        const inputData = parseJsonSafely(text as string);
        const content = inputData?.Content || text;
        return createContentPreview(
          text as string,
          () => {
            setCurrentContent(content as string);
            setInputModalVisible(true);
          },
          content as string
        );
      },
    },
    {
      title: '结果预览',
      dataIndex: 'result',
      search:false,
      ellipsis: false,
      width: 200,
      render: (text) => {
        const resultData = parseJsonSafely(text as string);
        const content = resultData?.default || text;
        return createContentPreview(
          text as string,
          () => {
            setCurrentContent(text as string);
            setResultModalVisible(true);
          },
          content as string
        );
      },
    },
    {
      title: '标题',
      dataIndex: 'title',
      width: 100,
      hideInTable: false,
      render: (text) => (
          <span className="font-mono text-xs text-gray-600 bg-gray-50 ">
          {text}
        </span>
      ),
    },
    {
      title: '音频时长',
      dataIndex: 'audioUsage',
      width: 60,
      search:false,
      render: (value) => value ? (
        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
          {value}s
        </span>
      ) : (
        <span className="text-gray-400 text-sm">-</span>
      ),
    },
    {
      title: '总分',
      dataIndex: 'totalScore',
      width: 80,
      search: false,
      render: (_, record: HistoryRecordWithEvaluation) => {
        if (!hasValidEvaluation(record)) {
          return <span className="text-gray-400 text-sm">-</span>;
        }
        
        return (
          <div className={`text-sm font-bold badge opacity-80 ${getScoreColor(record.totalScore, 100)}`}>{record.totalScore}</div>
        );
      },
    },
    {
      title: '子分数',
      dataIndex: 'subScores',
      width: 150,
      search: false,
      render: (_, record: HistoryRecordWithEvaluation) => {
        if (!hasValidEvaluation(record)) {
          return <span className="text-gray-400 text-sm">-</span>;
        }
        
        const scoreItems = [
          { label: '信息', score: record.informationScore || 0, max: 60 },
          { label: '细节', score: record.detailScore || 0, max: 20 },
          { label: '逻辑', score: record.logicScore || 0, max: 10 },
          { label: '语法', score: record.grammarScore || 0, max: 10 }
        ];
        
        return (
          <div className="flex flex-wrap gap-1">
            {scoreItems.map(({ label, score, max }) => (
              // <Tag key={label} className={`text-xs font-medium ${getScoreColor(score, max)}`}>
              //   {label}: {score}
              // </Tag>
              <div className={`text-sm font-bold badge badge-xs opacity-50 w-18 text-left  ${getScoreColor(record.totalScore, 100)}`}>
                {label}: {score}
                </div>

            ))}
          </div>
        );
      },
    },
    {
      title: '操作',
      valueType: 'option',
      width: 90,
      render: (_, record: HistoryRecordWithEvaluation) => {
        const isPending = record.evaluationStatus === 'PENDING' || evaluatingRecords.has(record.id);
        const isSuccess = record.evaluationStatus === 'SUCCESS';
        const isFailed = record.evaluationStatus === 'FAILED';
        
        return (
          <div className="flex flex-col gap-1">
            {isSuccess ? (
              <button
                className="btn btn-xs btn-accent w-full"
                onClick={() => {
                  window.open(`https://test.nivowork.com/evaluation/${record.id}`, '_blank');
                }}
              >
                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                </svg>
                查看评测
              </button>
            ) : isPending ? (
              <button
                className="btn btn-xs btn-outline btn-info w-full"
                disabled
              >
                <Spin
                  indicator={<LoadingOutlined className="w-3 h-3" spin />}
                  size="small"
                />
                评测中...
              </button>
            ) : (
              <button
                className={`btn btn-xs btn-outline w-full ${
                  isFailed ? 'btn-error' : 'btn-warning'
                }`}
                onClick={async () => {
                  try {
                    setEvaluatingRecords(prev => new Set(prev).add(record.id));
                    await HistoryAnalysisApi.reEvaluate(record.id);
                    message.success('重新评测请求已提交');
                    // 延迟刷新，给后端一些时间处理
                    setTimeout(() => {
                      actionRef.current?.reload();
                      setEvaluatingRecords(prev => {
                        const newSet = new Set(prev);
                        newSet.delete(record.id);
                        return newSet;
                      });
                    }, 1000);
                  } catch (error) {
                    setEvaluatingRecords(prev => {
                      const newSet = new Set(prev);
                      newSet.delete(record.id);
                      return newSet;
                    });
                    message.error('重新评测失败，请稍后重试');
                  }
                }}
              >
                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
                {isFailed ? '重新评测' : '开始评测'}
              </button>
            )}
          </div>
        );
      },
    },
  ];

  // 获取数据的函数
  const fetchData = async (params: any) => {
    console.log("fetchData",params,isAuthenticated,user);
    if (!isAuthenticated ) {
      return {
        data: [],
        success: false,
        total: 0,
      };
    }

    try {
      const queryParams: HistoryQueryParams = {
        // userId: user.sub,
        current: params.current,
        pageSize: params.pageSize,
      };

      // 处理ID筛选
      if (params.id && params.id.trim()) {
        queryParams.id = params.id.trim();
      }

      // 处理时间范围筛选
      if (params.startTime && params.endTime) {
        queryParams.startTime = dayjs(params.startTime).unix() * 1000;
        queryParams.endTime = dayjs(params.endTime).unix() * 1000;
      } else {
        // 默认为近七天
        const endTime = dayjs();
        const startTime = endTime.subtract(7, 'day');
        queryParams.startTime = startTime.unix() * 1000;
        queryParams.endTime = endTime.unix() * 1000;
      }

      const response = await HistoryAnalysisApi.getHistoryListWithEvaluation(queryParams);
      
      if (response.data.success) {
        return {
          data: response.data.data.list,
          success: true,
          total: response.data.data.total,
        };
      } else {
        handleError(new Error(response.data.message || '获取数据失败'), '获取历史记录失败');
        return {
          data: [],
          success: false,
          total: 0,
        };
      }
    } catch (error) {
      handleError(error, '网络请求失败');
      return {
        data: [],
        success: false,
        total: 0,
      };
    }
  };



  if (!isAuthenticated) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <p className="text-gray-500 mb-4">请先登录以查看历史记录分析</p>
          <Button type="primary">登录</Button>
        </div>
      </div>
    );
  }
  const [activeKey, setActiveKey] = useState<React.Key>('tab1');
  const renderBadge = (count: number, active = false) => {
    return (
      <Badge
        count={count}
        style={{
          marginBlockStart: -2,
          marginInlineStart: 4,
          color: active ? '#1890FF' : '#999',
          backgroundColor: active ? '#E6F7FF' : '#eee',
        }}
      />
    );
  };
  return (
    <div className=" min-h-screen">
      <Card className="shadow-sm">
        <div className="mb-0">
          <h1 className="text-2xl font-bold text-gray-800 mb-1">历史记录分析</h1>
        </div>

        <ProTable<HistoryRecord>
          columns={columns}
          actionRef={actionRef}
          request={fetchData}
          rowKey="id"
          search={{
            labelWidth: 'auto',
            defaultCollapsed: false,
          }}
          form={{
            syncToUrl: true,
            syncToInitialValues: false,
            initialValues: {
              createTime: [dayjs().subtract(7, 'day'), dayjs()],
            },
          }}
          pagination={{
            defaultPageSize: 10,
            showSizeChanger: true,
            showQuickJumper: true,
          }}

          toolbar={{
            menu: {
              type: 'tab',
              activeKey: activeKey,
              items: [
                {
                  key: 'tab1',
                  label: <span>A类{renderBadge(0, activeKey === 'tab1')}</span>,
                },
                {
                  key: 'tab2',
                  label: <span>B类{renderBadge(0, activeKey === 'tab2')}</span>,
                },
                
              ],
              onChange: (key) => {
                setActiveKey(key as string);
              },
            },
            actions: [
              // <Button key="primary" type="primary">
                
              // </Button>,
            ],
          }}

          scroll={{ x: 1200 }}
          size="small"
        />
      </Card>

      {/* 输入内容预览弹窗 */}
      <Modal
        title="输入内容详情"
        open={inputModalVisible}
        onCancel={() => setInputModalVisible(false)}
        footer={[
          <Button key="close" onClick={() => setInputModalVisible(false)}>
            关闭
          </Button>,
        ]}
        width={800}
      >
        <div className="mt-2 p-3 bg-gray-50 rounded max-h-96 overflow-y-auto">
          <pre className="whitespace-pre-wrap text-sm">
            {(() => {
              const parsed = parseJsonSafely(currentContent);
              return parsed ? JSON.stringify(parsed, null, 2) : currentContent;
            })()}
          </pre>
        </div>
      </Modal>

      {/* 结果内容预览弹窗 */}
      <Modal
        title="结果内容详情"
        open={resultModalVisible}
        onCancel={() => setResultModalVisible(false)}
        footer={[
          <Button key="close" onClick={() => setResultModalVisible(false)}>
            关闭
          </Button>,
        ]}
        width={800}
      >
        <div className="mt-2 p-3 bg-gray-50 rounded max-h-96 overflow-y-auto">
          <pre className="whitespace-pre-wrap text-sm">
            {(() => {
              const parsed = parseJsonSafely(currentContent);
              return parsed?.default || currentContent;
            })()}
          </pre>
        </div>
      </Modal>
    </div>
  );
};

export default AnalysisPage;